/* eslint-disable complexity */
import { FC, MouseEvent, ReactNode, SyntheticEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { Box, IconButton, MenuItem, SxProps, Theme, Typography, useMediaQuery } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
	ALLOWED_EVENT_AVATAR_MIME_TYPES,
	CreateEventDto,
	EventBundleDto,
	EventInstanceDto,
	EventPeriodType,
	EventPublishingStateEnum,
	EventTicketsTotalsWithReservationsDto,
	FileUploadDto,
	MembershipDto,
	PrivacyTypesEnum,
	ScheduleRepeatTypesEnum,
	UpdateEventToFitnessTypeDto,
	UserDto,
} from '@sweatpals/shared';
import { uniqBy } from 'lodash-es';
import { ThemeProvider } from '@mui/system';
import { FieldErrors, FieldValues, FormProvider, UseFormReturn } from 'react-hook-form';
import { commonFlowStyles } from '../../community-flow/create-community/create-community-flow.styles';
import {
	applyTzForDate,
	Button,
	CoreButtonBase,
	CoreTooltip,
	EventBackground,
	GetAddressInfoFromAddressCallback,
	Photo,
	prepareEventDataBeforeSubmit,
	QuestionnaireModalTypeEnum,
	Select,
	Showable,
	UserAvatar,
} from '../../';
import { createEventFlowStyles, textStyle } from './create-event-flow.styles';
import { AttendeeRegistrationFieldTypeEnum, Colors, EventTypeEnum } from '../../../constants';
import { clsx, GetPlacesCallback } from '../../../utils';
import { CoreConfirmationModal, Option } from '../../_core';
import { RepeatScheduleModal } from '../repeat-schedule-modal/repeat-schedule-modal.component';
import { useCustomRegister } from '../../../hooks';
import { QuestionnaireModal } from '../attendee-registration/questionnaire-modal.component';
import { CreateEventFormData, TiersTypeEnum } from '../../../models/dto';
import { darkTheme } from '../../../theme';
import {
	ArrowBackIcon,
	CalendarIcon,
	ChainIcon,
	CheckIcon,
	EditIcon,
	GlobeIcon,
	ImageIcon,
	PlusIcon,
	UserFilled,
} from '../../../icons';
import { PricesModal } from '../prices-modal/prices-modal.component';
import { flexRow } from '../../../styles';
import { AdvancedSettings } from './advanced-settings.component';
import { MainInfo } from './main-info.component';
import { WaiverModal } from '../waiver-modal/waiver-modal.component';
import { SponsorsModal } from '../sponsors-modal/sponsors-modal.component';
import { getOldActivityValues } from '../../activity-autocomplete/activity-autocomplete.component';
import { EventBundleModal } from '../event-bundle-modal/event-bundle-modal.component';

export interface CreateEventFlowProps<TFieldValues extends FieldValues = CreateEventFormData> {
	form: UseFormReturn<TFieldValues>;
	event?: EventInstanceDto;
	isDateFieldsDisabled?: boolean;
	initialFormValues?: CreateEventFormData;
	eventPublishingState?: EventPublishingStateEnum;
	disableSubmit?: boolean;
	loading?: boolean;
	periodType?: EventPeriodType;
	header?: ReactNode;
	hideHeader?: boolean;
	questionnaireImages: Record<AttendeeRegistrationFieldTypeEnum, string>;
	activityOptions?: Option[];
	getPlaceOptions: GetPlacesCallback;
	getAddressInfoFromAddress: GetAddressInfoFromAddressCallback;
	bodySx?: SxProps<Theme>;
	communityProfiles?: UserDto[];
	showAddCoHostButton?: boolean;
	isEditMode?: boolean;
	disableActivitiesAutocomplete?: boolean;
	shouldPricesModalBeOpen?: boolean;
	shouldPromocodesModalBeOpen?: boolean;
	communityMemberships?: MembershipDto[] | undefined;
	communityEventBundles?: EventBundleDto[] | undefined;
	eventTicketsCountAndSpotsWithReservations?: EventTicketsTotalsWithReservationsDto;
	showSubHeaderInput?: boolean | undefined;
	editOnlyThisEventUrl?: string;
	isEventIntegrationModal?: boolean;
	setIsEventIntegrationModal?(value: boolean): void;
	onAddCoHostButtonClick?(e: MouseEvent<HTMLButtonElement>): void;
	onAddDescriptionButtonClick?(e: MouseEvent<HTMLButtonElement>): void;
	onAddSubheaderButtonClick?(e: MouseEvent<HTMLButtonElement>): void;
	onPlaceInputFocus?(event: SyntheticEvent): void;
	getPublicFileUrl(fileId: string): string;
	onBackClick?(): void;
	onSubmit?(data: CreateEventDto, onlySave?: boolean): Promise<void> | void;
}

export const CreateEventFlow: FC<CreateEventFlowProps> = ({
	form,
	event,
	initialFormValues,
	onSubmit,
	disableSubmit,
	loading,
	header,
	periodType,
	isDateFieldsDisabled,
	eventPublishingState,
	activityOptions = [],
	questionnaireImages,
	onBackClick,
	getPlaceOptions,
	getAddressInfoFromAddress,
	onPlaceInputFocus,
	getPublicFileUrl,
	isEditMode,
	disableActivitiesAutocomplete = isEditMode,
	showAddCoHostButton = false,
	onAddCoHostButtonClick,
	onAddDescriptionButtonClick,
	onAddSubheaderButtonClick,
	shouldPricesModalBeOpen = false,
	shouldPromocodesModalBeOpen = false,
	communityProfiles = [],
	hideHeader = false,
	bodySx,
	communityMemberships,
	communityEventBundles,
	showSubHeaderInput,
	eventTicketsCountAndSpotsWithReservations,
	editOnlyThisEventUrl,
	isEventIntegrationModal,
	setIsEventIntegrationModal,
}) => {
	const isDraft = useMemo(
		() => (!eventPublishingState || eventPublishingState === EventPublishingStateEnum.Draft),
		[eventPublishingState]
	);
	const isLessThanSmall = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

	const data = form.watch();

	const [confirmCloseCallback, setConfirmCloseCallback] = useState<() => void>();

	const tiersOptions = useMemo<Option[]>(
		() => (data.tiers ?? []).filter(t => t.tierName).map(({ key, tierName }) => ({ value: key, label: tierName })),
		[data.tiers]
	);
	const eventBackgroundUrl = useMemo(() => data?.avatarImg?.base64EncodedFile, [data?.avatarImg?.base64EncodedFile]);

	// auto fill event activities based on event name and description words
	useEffect(() => {
		if (disableActivitiesAutocomplete) return;

		const eventName = data?.name;
		const eventDescription = data?.description;
		const fitnessTypes = form.getValues('fitnessTypes');
		const currentFitnessTypeIds = fitnessTypes?.filter(type => !type.delete).map(type => type.fitnessTypeId);

		if (!eventName && !eventDescription) {
			return;
		}

		const matchingActivityTypes = activityOptions.filter(
			activity =>
				eventName?.toLowerCase().includes(activity.label.toLowerCase()) ||
				eventDescription?.toLowerCase().includes(activity.label.toLowerCase())
		);

		const newFitnessTypes = matchingActivityTypes
			?.filter(type => !currentFitnessTypeIds?.includes(type.value))
			.slice(0, 1) // only autofill one activity type
			.map(
				option =>
					({
						fitnessTypeId: option.value,
						masteryLevel: 0,
					} as UpdateEventToFitnessTypeDto)
			);

		if (newFitnessTypes?.length) {
			// remove deleted types if they are added again
			const oldTypes = getOldActivityValues(newFitnessTypes.map(t => t.fitnessTypeId), fitnessTypes);
			// calc uniq types
			const newUniqTypes = uniqBy([...(oldTypes ?? []), ...newFitnessTypes], 'fitnessTypeId');
			form.setValue('fitnessTypes', newUniqTypes);
		}
	}, [disableActivitiesAutocomplete, data?.name, data?.description, activityOptions, form]);

	const privacyDescription = useMemo(() => {
		if (data?.privacyId === PrivacyTypesEnum.Public) {
			return 'Everyone can see and RSVP to your event';
		} else if (data?.privacyId === PrivacyTypesEnum.OnlyMe) {
			return 'Anyone with the link can RSVP but it’s not discoverable on the app';
		} else if (data?.privacyId === PrivacyTypesEnum.OnlyPals) {
			return 'Only members of your community can see and RSVP to your event';
		}

		return undefined;
	}, [data?.privacyId]);

	const { register } = useCustomRegister(form);

	const [isRepeatScheduleModalOpen, setIsRepeatScheduleModalOpen] = useState(false);
	const [isPricesModalOpen, setIsPricesModalOpen] = useState(shouldPricesModalBeOpen || shouldPromocodesModalBeOpen);
	const [isPromocodesModalOpen, setIsPromocodesModalOpen] = useState(shouldPromocodesModalBeOpen);
	const [isQuestionnaireModalOpen, setIsQuestionnaireModalOpen] = useState(false);
	const [isWaiverModalOpen, setIsWaiverModalOpen] = useState(false);
	const [isEventBundleModalOpen, setIsEventBundleModalOpen] = useState(false);
	const [isConfirmClearRepeatModalOpen, setIsConfirmClearRepeatModalOpen] = useState(false);
	const [isSponsorsModalOpen, setIsSponsorsModalOpen] = useState(false);
	const [isQuestionnaireInvalid, setIsQuestionnaireInvalid] = useState(false);

	const submitEventForm = useCallback(
		(isDraft?: boolean, onlySave?: boolean) => async (submitData: typeof data) => {
			console.debug('create-event-flow.handleSubmit', {
				isDraft,
				onlySave,
				submitData,
			})

			if (!submitData) {
				return;
			}

			const preparedData: CreateEventDto = await prepareEventDataBeforeSubmit({
				event,
				isDraft,
				isEditMode,
				data: submitData,
				initialData: initialFormValues,
				getAddressInfoFromAddress,
			});

			await onSubmit?.(preparedData, onlySave);
		},
		[getAddressInfoFromAddress, onSubmit, initialFormValues, event, isEditMode]
	);
	const onInvalid = useCallback((errors: FieldErrors<CreateEventFormData>): void => {
		if (errors.attendeeRegistration) {
			setIsQuestionnaireModalOpen(true);
			setIsQuestionnaireInvalid(true);
		}

		console.warn('Errors: ', errors);
	}, []);

	const styles = commonFlowStyles();
	const eventFlowStyles = createEventFlowStyles();

	const boughtTicketsByTier = useMemo(() => {
		if (!event) {
			return {};
		}

		return event?.prices?.reduce((acc, price) => {
			acc[price?.id] = eventTicketsCountAndSpotsWithReservations?.pricesTicketsCount?.find(p => p.basePriceId === price?.basePriceId)?.ticketsCount ?? 0;
			return acc;
		}, {} as Record<string, number>);
	}, [eventTicketsCountAndSpotsWithReservations, event]);

	const promocodesUsesById = useMemo(() => {
		if (!event?.discounts?.length) {
			return {};
		}

		return event.discounts.reduce<Record<string, number>>((acc, discount) => ({
			...acc,
			[discount.id]: discount.uses,
		}), {});
	}, [event]);

	return (
		<FormProvider {...form}>
			<ThemeProvider theme={darkTheme}>
				<LocalizationProvider dateAdapter={AdapterDateFns}>
					<Box component='form' sx={styles.root}>
						<EventBackground background={eventBackgroundUrl} />

						<Box sx={clsx(styles.content, eventFlowStyles.content)}>
							{!hideHeader && (
								<Box sx={styles.header}>
									<IconButton sx={styles.headerBackButton} onClick={onBackClick}>
										<ArrowBackIcon style={eventFlowStyles.backIcon} />
									</IconButton>

									<Box sx={styles.headerContent}>{header}</Box>
								</Box>
							)}

							<Box sx={clsx(eventFlowStyles.body, bodySx)}>
								<Box
									sx={{
										flex: 1,
										position: 'relative',
										display: 'flex',
										flexDirection: 'column',
										alignItems: 'center',
										justifyContent: 'space-between',
									}}
								>
									<Box
										sx={{
											position: 'relative',
											p: { xs: '24px 20px', sm: '50px', lg: '80px 70px 80px 125px', xl: '80px 125px 80px 125px' },

											maxWidth: { xs: 'none', md: '518px' },
											boxSizing: 'content-box',

											display: 'flex',
											flexDirection: 'column',
											gap: { xs: '16px', md: '24px' },
										}}
									>
										<Box display='flex'>
											<Showable show={communityProfiles.length}>
												<Select
													value={data?.authorId}
													onChange={e => {
														form.setValue('authorId', e.target.value as string | undefined);
													}}
													SelectDisplayProps={{
														style: { paddingRight: 30 },
													}}
													disabled={isEditMode}
													renderValue={value => {
														const community = communityProfiles?.find(p => p.id === value);

														if (!community) {
															return null;
														}

														return (
															<Box sx={flexRow({ width: '100%', gap: '8px', alignItems: 'center' })}>
																<UserAvatar
																	fullName={community.fullName}
																	avatarSrc={community.avatarId && getPublicFileUrl(community.avatarId)}
																	emptyAvatarVariant={community.emptyAvatarVariant}
																	sx={{ width: '28px', height: '28px' }}
																/>

																<Typography
																	sx={{
																		flex: 1,
																		fontFamily: 'Hind',
																		fontSize: '14px',
																		fontWeight: 500,
																		lineHeight: '14px',
																		overflow: 'hidden',
																		textOverflow: 'ellipsis',
																		whiteSpace: 'nowrap',
																	}}
																>
																	{community.fullName}
																</Typography>
															</Box>
														);
													}}
													controlSx={{ flex: 1 }}
													sx={eventFlowStyles.communitySelect}
												>
													{communityProfiles?.map(community => (
														<MenuItem
															key={community.id}
															value={community.id}
															sx={flexRow({
																alignItems: 'center',
															})}
														>
															<UserAvatar
																fullName={community.fullName}
																avatarSrc={community.avatarId && getPublicFileUrl(community.avatarId)}
																emptyAvatarVariant={community.emptyAvatarVariant}
																sx={{ width: '24px', height: '24px' }}
															/>

															<Typography
																sx={textStyle({
																	flexGrow: 1,
																	fontSize: '12px',
																	fontWeight: '500',
																})}
															>
																{community.fullName}
															</Typography>

															<Box
																component={CheckIcon}
																sx={{
																	color: Colors.white,
																	display: data?.authorId === community.id ? 'block' : 'none',
																}}
															/>
														</MenuItem>
													))}
												</Select>
											</Showable>
											<Box
												sx={flexRow({
													justifyContent: 'flex-end',
													alignItems: 'center',
													width: communityProfiles.length ? 'fit-content' : '100%',
												})}
											>
												<Showable show={showAddCoHostButton}>
													<CoreButtonBase sx={eventFlowStyles.addCohostButton} onClick={onAddCoHostButtonClick}>
														{data.cohost ? (
															<>
																<UserAvatar
																	fullName={data.cohost.fullName}
																	avatarSrc={data.cohost.avatarId && getPublicFileUrl(data.cohost.avatarId)}
																	emptyAvatarVariant={data.cohost.emptyAvatarVariant}
																	sx={{ width: '24px', height: '24px' }}
																/>
																<Typography>{data.cohost.fullName}</Typography>
															</>
														) : (
															<>
																<PlusIcon />
																<Typography>Add Co-host</Typography>
															</>
														)}
													</CoreButtonBase>
												</Showable>
												<CoreTooltip
													title={privacyDescription}
													arrow
													placement={isLessThanSmall ? 'top-end' : 'left-start'}
													dark
												>
													<Select
														value={data?.privacyId}
														onChange={e => {
															const newValue = e.target.value as PrivacyTypesEnum;
															form.setValue('privacyId', newValue);

															if (newValue === PrivacyTypesEnum.OnlyMe) {
																form.setValue('isCommunityChatSharingEnabled', false);
															}
														}}
														renderValue={value => (
															<Box sx={flexRow({ gap: '4px', alignItems: 'center' })}>
																<Box
																	component={
																		value === PrivacyTypesEnum.Public
																			? GlobeIcon
																			: value === PrivacyTypesEnum.OnlyMe
																			? ChainIcon
																			: UserFilled
																	}
																	sx={{
																		width: '20px',
																	}}
																/>

																<Typography
																	sx={textStyle({
																		fontWeight: '600',
																	})}
																>
																	{value === PrivacyTypesEnum.Public
																		? 'Public'
																		: value === PrivacyTypesEnum.OnlyMe
																		? 'Unlisted'
																		: 'Only Members'}
																</Typography>
															</Box>
														)}
														sx={eventFlowStyles.privacySelect}
														IconComponent={() => null}
													>
														<MenuItem
															value={PrivacyTypesEnum.Public}
															sx={flexRow({
																alignItems: 'center',
															})}
														>
															<Box
																component={GlobeIcon}
																sx={{
																	color: Colors.white,
																	width: '16px',
																	height: '16px',
																}}
															/>

															<Typography
																sx={textStyle({
																	flexGrow: 1,
																	fontSize: '12px',
																	fontWeight: '500',
																})}
															>
																Public
															</Typography>

															<Box
																component={CheckIcon}
																sx={{
																	color: Colors.white,
																	display: data?.privacyId === PrivacyTypesEnum.Public ? 'block' : 'none',
																}}
															/>
														</MenuItem>
														<MenuItem
															value={PrivacyTypesEnum.OnlyMe}
															sx={flexRow({
																alignItems: 'center',
															})}
														>
															<Box
																component={ChainIcon}
																sx={{
																	color: Colors.white,
																	width: '16px',
																	height: '16px',
																}}
															/>

															<Typography
																sx={textStyle({
																	flexGrow: 1,
																	fontSize: '12px',
																	fontWeight: '500',
																})}
															>
																Unlisted
															</Typography>

															<Box
																component={CheckIcon}
																sx={{
																	color: Colors.white,
																	display: data?.privacyId === PrivacyTypesEnum.OnlyMe ? 'block' : 'none',
																}}
															/>
														</MenuItem>
														<MenuItem
															value={PrivacyTypesEnum.OnlyPals}
															sx={flexRow({
																alignItems: 'center',
															})}
														>
															<Box
																component={UserFilled}
																sx={{
																	color: Colors.white,
																	width: '16px',
																	height: '16px',
																}}
															/>

															<Typography
																sx={textStyle({
																	flexGrow: 1,
																	fontSize: '12px',
																	fontWeight: '500',
																})}
															>
																Only Members
															</Typography>

															<Box
																component={CheckIcon}
																sx={{
																	color: Colors.white,
																	display: data?.privacyId === PrivacyTypesEnum.OnlyPals ? 'block' : 'none',
																}}
															/>
														</MenuItem>
													</Select>
												</CoreTooltip>
											</Box>
										</Box>

										<MainInfo
											isDateFieldsDisabled={isDateFieldsDisabled}
											activityOptions={activityOptions}
											getPlaceOptions={getPlaceOptions}
											onPlaceInputFocus={onPlaceInputFocus}
											periodType={periodType}
											editOnlyThisEventUrl={editOnlyThisEventUrl}
											setIsConfirmClearRepeatModalOpen={setIsConfirmClearRepeatModalOpen}
											setIsPromocodesModalOpen={setIsPromocodesModalOpen}
											setIsRepeatScheduleModalOpen={setIsRepeatScheduleModalOpen}
											setIsPricesModalOpen={setIsPricesModalOpen}
											onAddDescriptionButtonClick={onAddDescriptionButtonClick}
											onAddSubheaderButtonClick={onAddSubheaderButtonClick}
											showRepeatScheduleModal={isRepeatScheduleModalOpen}
											showSubHeaderInput={showSubHeaderInput}
										/>

										<AdvancedSettings
											isEditMode={isEditMode}
											isEventIntegrationModal={isEventIntegrationModal}
											setIsQuestionnaireModalOpen={setIsQuestionnaireModalOpen}
											setIsWaiverModalOpen={setIsWaiverModalOpen}
											setIsSponsorsModalOpen={setIsSponsorsModalOpen}
											setIsEventBundleModalOpen={setIsEventBundleModalOpen}
											setIsEventIntegrationModal={setIsEventIntegrationModal}
										/>
									</Box>
									<Box sx={clsx(eventFlowStyles.footer, { px: 0 })}>
										<Box sx={clsx(eventFlowStyles.footerContent)}>
											<Button
												variant='secondary'
												disabled={disableSubmit}
												sx={eventFlowStyles.submitButton}
												loading={loading}
												strictColor
												onClick={() => {
													form.handleSubmit(submitEventForm(isDraft, true), onInvalid)();
												}}
											>
												{isDraft ? 'Save as Draft' : 'Save'}
											</Button>

											<Button
												disabled={disableSubmit}
												sx={eventFlowStyles.publishButton}
												loading={loading}
												strictColor
												onClick={() => {
													form.handleSubmit(submitEventForm(false), onInvalid)();
												}}
											>
												Publish event
											</Button>
										</Box>
									</Box>
								</Box>
								<Box
									sx={{
										width: { xs: '100%', lg: '600px', xl: '700px' },
										position: 'relative',
										display: 'flex',
										alignItems: 'flex-end',
									}}
								>
									<Box
										sx={{
											position: 'sticky',
											bottom: 0,
											left: 0,
											right: 0,
											width: '100%',
										}}
									>
										<Photo
											{...register('avatarImg', {
												passRef: false,
											})}
											onChange={(uploadFile: FileUploadDto | null) => {
												form.setValue('avatarImg', uploadFile ?? undefined);

												if (uploadFile === null) {
													form.setValue('avatarId', undefined);
												}
											}}
											previousValue={data?.avatarId ? getPublicFileUrl(data.avatarId) : undefined}
											accept={ALLOWED_EVENT_AVATAR_MIME_TYPES.reduce((acc, key) => ({ ...acc, [key]: [] }), {})}
											maxWidth={538}
											sx={eventFlowStyles.backgroundPhoto}
											wrapperSx={eventFlowStyles.backgroundBox(
												data?.avatarImg?.base64EncodedFile ||
													(data?.avatarId ? getPublicFileUrl(data.avatarId) : undefined)
											)}
											EditButton={({ open, disabled }) => (
												<Box sx={eventFlowStyles.changeImageButtonWrapper}>
													<Box sx={eventFlowStyles.imageSizeText}>Recommended size: 1080 x 1350px</Box>
													<CoreButtonBase
														disabled={disabled}
														sx={eventFlowStyles.changeImageButton}
														onClick={e => {
															e.stopPropagation();

															open();
														}}
													>
														<ImageIcon />
														<Typography>Change image</Typography>
													</CoreButtonBase>
												</Box>
											)}
											isDeleteButtonHidden
											withWrapper
										/>
									</Box>
								</Box>
							</Box>

							{isRepeatScheduleModalOpen && data?.startDate && (
								<RepeatScheduleModal
									open={isRepeatScheduleModalOpen}
									data={data?.scheduleRepeat}
									eventStartDate={applyTzForDate(data.startDate, 'UTC', data.tzid)}
									CalendarIcon={CalendarIcon}
									onClose={isDirty => {
										const closeHandler = (): void => {
											setIsRepeatScheduleModalOpen(false);
										};

										if (isDirty) {
											setConfirmCloseCallback(() => closeHandler);
										} else {
											closeHandler();
										}
									}}
									onSubmit={data => {
										form.setValue('scheduleRepeat', data);
										setIsRepeatScheduleModalOpen(false);
									}}
								/>
							)}

							<CoreConfirmationModal
								icon={EditIcon}
								open={isConfirmClearRepeatModalOpen}
								onCancel={() => {
									setIsConfirmClearRepeatModalOpen(false);
								}}
								onConfirm={() => {
									form.setValue('scheduleRepeat', { typeId: ScheduleRepeatTypesEnum.Never });
									setIsConfirmClearRepeatModalOpen(false);
								}}
								title='Clear Event Repeat Settings'
								message='Are you sure you want to clear event repeat settings?'
							/>

							<Showable show={isPricesModalOpen}>
								<PricesModal
									open={isPricesModalOpen}
									data={{
										type: data?.type,
										tiers: data?.tiers,
										discounts: data?.discounts,
										withAttendeesLimit: data?.withAttendeesLimit,
										minimalDonationAmount: data?.minimalDonationAmount,
										attendeesLimit: data?.attendeesLimit,
									}}
									onClose={isDirty => {
										const closeHandler = (): void => {
											setIsPricesModalOpen(false);
										};

										if (isDirty) {
											setConfirmCloseCallback(() => closeHandler);
										} else {
											closeHandler();
										}
									}}
									setConfirmCloseCallback={setConfirmCloseCallback}
									onSubmit={submitData => {
										if (data.attendeeRegistration) {
											const tierKeys = submitData.tiers?.map(tier => tier.key) ?? [];
											const newAttendeeRegistrationFields = (data.attendeeRegistration.fields ?? [])
												.map(field => ({
													...field,
													tierIds: field.tierIds?.filter(tierId => tierKeys.includes(tierId)),
												}))
												.filter(field => field.tiersType !== TiersTypeEnum.Selected || field.tierIds?.length);

											form.setValue('attendeeRegistration.fields', newAttendeeRegistrationFields);
											form.setValue('withQuestionnaire', !!newAttendeeRegistrationFields.length);
										}

										form.setValue('type', submitData.type);
										form.setValue('tiers', submitData.tiers);
										form.setValue('discounts', submitData.discounts);
										form.setValue('minimalDonationAmount', submitData.minimalDonationAmount);
										form.setValue('attendeesLimit', submitData.attendeesLimit);
										form.setValue('withAttendeesLimit', submitData.withAttendeesLimit);
										setIsPricesModalOpen(false);
										setIsPromocodesModalOpen(false);
									}}
									showPromocodesModal={isPromocodesModalOpen}
									openPromocodesModal={() => {
										setIsPromocodesModalOpen(true);
									}}
									closePromocodesModal={() => {
										setIsPromocodesModalOpen(false);
									}}
									communityMemberships={communityMemberships}
									boughtTicketsByTier={boughtTicketsByTier}
									promocodesUsesById={promocodesUsesById}
								/>
							</Showable>

							<Showable show={isQuestionnaireModalOpen}>
								<QuestionnaireModal
									open={isQuestionnaireModalOpen}
									type={QuestionnaireModalTypeEnum.Event}
									initialData={data?.attendeeRegistration}
									shouldValidateInitialData={isQuestionnaireInvalid}
									isPaidEvent={EventTypeEnum.Paid === data?.type}
									onClose={isDirty => {
										const closeHandler = (): void => {
											setIsQuestionnaireModalOpen(false);

											if (!data?.attendeeRegistration?.fields?.length) {
												form.setValue('withQuestionnaire', false);
											}
										};

										if (isDirty) {
											setConfirmCloseCallback(() => closeHandler);
										} else {
											closeHandler();
										}
									}}
									onSubmit={data => {
										form.setValue('attendeeRegistration', data);
										setIsQuestionnaireModalOpen(false);
										setIsQuestionnaireInvalid(false);

										if (!data?.fields?.length) {
											form.setValue('withQuestionnaire', false);
										}
									}}
									tiersOptions={tiersOptions}
									questionnaireImages={questionnaireImages}
								/>
							</Showable>

							<Showable show={isWaiverModalOpen}>
								<WaiverModal
									open={isWaiverModalOpen}
									data={data?.waiver}
									onClose={isDirty => {
										const closeHandler = (): void => {
											setIsWaiverModalOpen(false);

											if (!data?.waiver && data?.withWaiver) {
												form.setValue('withWaiver', false);
											}
										};

										if (isDirty) {
											setConfirmCloseCallback(() => closeHandler);
										} else {
											closeHandler();
										}
									}}
									onSubmit={data => {
										form.setValue('waiver', data);
										setIsWaiverModalOpen(false);
									}}
								/>
							</Showable>

							<Showable show={isEventBundleModalOpen && communityEventBundles}>
								<EventBundleModal
									open={isEventBundleModalOpen}
									communityEventBundles={communityEventBundles ?? []}
									data={data?.eventBundleIds}
									onClose={isDirty => {
										const closeHandler = (): void => {
											setIsEventBundleModalOpen(false);
											if (!data?.eventBundleIds && data?.withEventBundle) {
												form.setValue('withEventBundle', false);
											}
										};

										if (isDirty) {
											setConfirmCloseCallback(() => closeHandler);
										} else {
											closeHandler();
										}
									}}
									onSubmit={data => {
										form.setValue('eventBundleIds', data);
										setIsEventBundleModalOpen(false);
									}}
								/>
							</Showable>

							<Showable show={isSponsorsModalOpen}>
								<SponsorsModal
									open={isSponsorsModalOpen}
									onClose={isDirty => {
										const closeHandler = (): void => {
											setIsSponsorsModalOpen(false);
										};

										if (isDirty) {
											setConfirmCloseCallback(() => closeHandler);
										} else {
											closeHandler();
										}
									}}
									onSubmit={data => {
										form.setValue('sponsors', data.sponsors);
										setIsSponsorsModalOpen(false);
									}}
									data={data?.sponsors}
									getPublicFileUrl={getPublicFileUrl}
								/>
							</Showable>
						</Box>
					</Box>

					<CoreConfirmationModal
						title='Are you sure you want to close without saving changes?'
						message='Close without saving'
						open={!!confirmCloseCallback}
						onCancel={() => {
							setConfirmCloseCallback(undefined);
						}}
						onConfirm={() => {
							confirmCloseCallback?.();
							setConfirmCloseCallback(undefined);
						}}
					/>
				</LocalizationProvider>
			</ThemeProvider>
		</FormProvider>
	);
};
